import * as React from "react";
import { useGetBusinessReportsQuery } from "@/modules/snapshots/businessReports.api";
import { useGetSnapshotCollectionsQuery } from "@/modules/snapshots/snapshotCollection.api";
import type { BusinessReport } from "@/modules/snapshots/businessReports.types";
import type { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";

/**
 * Resolves the route's collection and its business report from the cached lists, keeping the last match for the
 * same id when a list temporarily stops containing it.
 */
export const useRetainedSnapshotCollection = (collectionId: string | undefined) => {
  const { data: snapshotCollections = [] } = useGetSnapshotCollectionsQuery();
  const { data: businessReports = [] } = useGetBusinessReportsQuery();
  const routeSnapshotCollection = snapshotCollections.find(row => row.id === collectionId);
  const [retainedSnapshotCollection, setRetainedSnapshotCollection] = React.useState<SnapshotCollection | null>(null);
  const snapshotCollection =
    routeSnapshotCollection ??
    (retainedSnapshotCollection?.id === collectionId ? retainedSnapshotCollection : undefined);
  const routeBusinessReport = businessReports.find(row => row.id === snapshotCollection?.business_report_id);
  const [retainedBusinessReport, setRetainedBusinessReport] = React.useState<BusinessReport | null>(null);
  const businessReport =
    routeBusinessReport ??
    (retainedBusinessReport?.id === snapshotCollection?.business_report_id ? retainedBusinessReport : undefined);
  const businessReportId = snapshotCollection?.business_report_id;

  React.useEffect(() => {
    if (routeSnapshotCollection) {
      setRetainedSnapshotCollection(routeSnapshotCollection);
    }
  }, [routeSnapshotCollection]);

  React.useEffect(() => {
    if (routeBusinessReport) {
      setRetainedBusinessReport(routeBusinessReport);
    }
  }, [routeBusinessReport]);

  const siblingCollections = React.useMemo(
    () => (businessReportId ? snapshotCollections.filter(c => c.business_report_id === businessReportId) : []),
    [snapshotCollections, businessReportId],
  );

  return { snapshotCollection, businessReport, businessReportId, siblingCollections };
};
